import * as React from 'react';
import { useRouter } from 'next/router';
import { Howl, Howler } from 'howler';

interface AudioContextType {
  bgm: string;
  bgmVolume: number;
  soundEffectVolume: number;
  isMutedBGM: boolean;
  isMutedSoundEffect: boolean;
  playBgm: () => void;
  pauseBgm: () => void;
  stopBgm: () => void;
  changeBgm: (bgm: string) => void;
  toggleMute: () => void;
  toggleSoundEffectMute: () => void;
  playSoundEffect: (key: string) => void;
  stopSoundEffect: (key: string) => void;
  changeBGMVolume: (volume: number) => void;
  changeSoundEffectVolume: (volume: number) => void;
}

const AudioContext = React.createContext<AudioContextType | undefined>(
  undefined,
);

interface AudioProviderProps {
  children: React.ReactNode;
}

Howler.autoSuspend = false;
Howler.autoUnlock = true; // iOSで最初のタップ時に自動アンロック

export const AudioProvider: React.FC<AudioProviderProps> = ({ children }) => {
  const router = useRouter();
  const [bgmVolume, setBGMVolume] = React.useState(1);
  const [isMutedBGM, setMutedBGM] = React.useState(false);
  const [isMutedSoundEffect, setMutedSoundEffect] = React.useState(false);
  const [isAudioUnlocked, setIsAudioUnlocked] = React.useState(false);

  const [soundEffectVolume, setSoundEffectVolume] = React.useState(0.8);
  const [bgm, setBgm] = React.useState('/audios/bgm_1.mp3');
  const audioRef = React.useRef<Howl>();
  // 全BGMをプリロードしておき、切替時はクロスフェードで途切れさせない
  const bgmHowlsRef = React.useRef<{ [path: string]: Howl }>({});
  const soundEffectsRef = React.useRef<{ [key: string]: Howl }>({});
  const isAudioUnlockedRef = React.useRef(false);
  const bgmPathRef = React.useRef('/audios/bgm_1.mp3');
  const isMutedBGMRef = React.useRef(false);
  const isMutedSoundEffectRef = React.useRef(false);
  const bgmVolumeRef = React.useRef(1);
  const resumeVerifyTimeoutRef = React.useRef<number | null>(null);
  // Flutterのアプリライフサイクルから受け取るバックグラウンド状態。
  // バックグラウンド中はBGMを一切再生しないために使う。
  const isBackgroundedRef = React.useRef(false);

  const clearResumeVerifyTimeout = React.useCallback(() => {
    if (resumeVerifyTimeoutRef.current != null) {
      window.clearTimeout(resumeVerifyTimeoutRef.current);
      resumeVerifyTimeoutRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    isMutedBGMRef.current = isMutedBGM;
  }, [isMutedBGM]);

  React.useEffect(() => {
    isMutedSoundEffectRef.current = isMutedSoundEffect;
  }, [isMutedSoundEffect]);

  React.useEffect(() => {
    bgmVolumeRef.current = bgmVolume;
  }, [bgmVolume]);

  // BGMを復帰させる。AudioContextをresumeしたうえでplayし、それでも鳴っていなければHowlを作り直す。
  // 連続して開閉した時に検証タイマーが古い状態で発火するレースを避けるため、
  // 既存の検証タイマーは毎回キャンセルし、バックグラウンド中はplay/再生成しない。
  const tryResumeBgm = React.useCallback(() => {
    if (!isAudioUnlockedRef.current) return;
    if (isMutedBGMRef.current) return;
    // Flutterから受け取ったバックグラウンド状態を最優先で見る
    if (isBackgroundedRef.current) return;
    if (audioRef.current?.playing()) return;

    clearResumeVerifyTimeout();

    const playAndVerify = () => {
      // resume()のPromise解決を待っている間にバックグラウンドへ行った場合は何もしない
      if (isBackgroundedRef.current) return;
      if (isMutedBGMRef.current) return;
      audioRef.current?.play();
      // iOSではAudioContextがinterruptedから戻ってもaudio graphが壊れていることがあるため、
      // 一定時間後に再生状態を確認し、鳴っていなければHowlを作り直す
      resumeVerifyTimeoutRef.current = window.setTimeout(() => {
        resumeVerifyTimeoutRef.current = null;
        if (isMutedBGMRef.current) return;
        // 検証中にバックグラウンドへ行った場合は再生成しない
        if (isBackgroundedRef.current) return;
        if (audioRef.current?.playing()) return;
        // 壊れたHowlを作り直す。プリロードキャッシュも更新する。
        audioRef.current?.unload();
        const recreated = new Howl({
          src: [bgmPathRef.current],
          autoplay: true,
          loop: true,
          volume: bgmVolumeRef.current,
        });
        bgmHowlsRef.current[bgmPathRef.current] = recreated;
        audioRef.current = recreated;
      }, 400);
    };

    if (Howler.ctx && Howler.ctx.state !== 'running') {
      Howler.ctx.resume().then(playAndVerify).catch(playAndVerify);
    } else {
      playAndVerify();
    }
  }, [clearResumeVerifyTimeout]);

  // プリロード済みのHowlを取得（無ければ作成する。フォールバック用途）
  const ensureBgmHowl = (path: string): Howl => {
    let howl = bgmHowlsRef.current[path];
    if (!howl) {
      howl = new Howl({
        src: [path],
        autoplay: false,
        loop: true,
        volume: bgmVolumeRef.current,
      });
      bgmHowlsRef.current[path] = howl;
    }
    return howl;
  };

  const changeBgm = (newBgm: string) => {
    // 同じBGMなら何もしない（ページ遷移時の途切れ防止）
    if (bgmPathRef.current === newBgm && audioRef.current) return;

    const oldHowl = audioRef.current;
    const newHowl = ensureBgmHowl(newBgm);

    bgmPathRef.current = newBgm;
    setBgm(newBgm);
    audioRef.current = newHowl;

    // バックグラウンド/ミュート中は再生せず、旧BGMだけ止める
    if (isBackgroundedRef.current || isMutedBGMRef.current) {
      if (oldHowl && oldHowl !== newHowl) oldHowl.stop();
      return;
    }

    const fadeDuration = 500;
    const targetVolume = bgmVolumeRef.current;

    // 新BGMを音量0から再生してフェードイン
    newHowl.volume(0);
    if (!newHowl.playing()) {
      newHowl.play();
    }
    newHowl.fade(0, targetVolume, fadeDuration);

    // 旧BGMをフェードアウトしてからstop
    if (oldHowl && oldHowl !== newHowl) {
      const oldVolume = oldHowl.volume();
      oldHowl.fade(oldVolume, 0, fadeDuration);
      window.setTimeout(() => {
        oldHowl.stop();
        // 次回再生に備えてボリュームを戻しておく
        oldHowl.volume(targetVolume);
      }, fadeDuration + 50);
    }

    // autoplayが弾かれて無音のままになるケースのリカバリ
    window.setTimeout(() => {
      if (isMutedBGMRef.current) return;
      if (isBackgroundedRef.current) return;
      if (audioRef.current?.playing()) return;
      tryResumeBgm();
    }, 600);
  };

  const setupBgm = () => {
    const bgmVolume = localStorage.getItem('bgmVolume')
      ? Number(localStorage.getItem('bgmVolume'))
      : 1;
    const soundEffectVolume = localStorage.getItem('soundEffectVolume')
      ? Number(localStorage.getItem('soundEffectVolume'))
      : 1;
    setBGMVolume(bgmVolume);
    setSoundEffectVolume(soundEffectVolume);
    bgmVolumeRef.current = bgmVolume;

    // 全BGMをプリロード（切替時の無音ギャップを無くすため）
    const bgmFiles = ['/audios/bgm_1.mp3', '/audios/bgm_2.mp3'];
    for (const src of bgmFiles) {
      if (!bgmHowlsRef.current[src]) {
        bgmHowlsRef.current[src] = new Howl({
          src: [src],
          autoplay: false,
          loop: true,
          volume: bgmVolume,
        });
      }
    }

    // ルートに基づいて初期BGMを決定（room内はbgm_2、それ以外はbgm_1）
    const pathname = window.location.pathname;
    const isInRoom =
      pathname.startsWith('/rooms/') || pathname.startsWith('/turn-rooms/');
    const bgmSource = isInRoom ? '/audios/bgm_2.mp3' : '/audios/bgm_1.mp3';
    bgmPathRef.current = bgmSource;
    setBgm(bgmSource);
    audioRef.current = bgmHowlsRef.current[bgmSource];

    const soundEffectFiles: { [key: string]: string } = {
      timer: '/audios/timer_60s.mp3',
      post: '/audios/post.mp3',
      correct_answer: '/audios/correct.mp3',
      question_start: '/audios/question_start.mp3',
      question_end: '/audios/question_end.mp3',
      incorrect_answer: '/audios/incorrect.mp3',
      almost_correct: '/audios/almost_correct.mp3',
      no_life: '/audios/no_life.mp3',
      typing: '/audios/typing.mp3',
      click_tab: '/audios/click_tab.mp3',
      click_button: '/audios/click_button.mp3',
    };

    for (const [key, src] of Object.entries(soundEffectFiles)) {
      soundEffectsRef.current[key] = new Howl({
        src: [src],
        autoplay: false,
        loop: false,
        volume: 0.5,
      });
    }
    if (!audioRef.current) return;
    // バックグラウンド中なら再生しない
    if (isBackgroundedRef.current) return;
    try {
      audioRef.current.volume(bgmVolume);
      audioRef.current.play();
    } catch (error) {
      console.log('Error playing audio:', error);
    }
  };

  // Flutterから呼び出せるグローバル関数を登録
  React.useEffect(() => {
    // Flutterから音声を開始するための関数
    (window as any).unlockAndPlayAudio = () => {
      if (isAudioUnlockedRef.current) return;
      isAudioUnlockedRef.current = true;
      setIsAudioUnlocked(true);
      setupBgm();
    };

    // Flutterからバックグラウンド復帰時に音声を再開するための関数
    // Flutter側のdidChangeAppLifecycleState(resumed)から呼ばれる。
    // visibilityState更新やAudioContext復帰の遅延を吸収するため少し待ってからリトライする。
    (window as any).resumeAudio = () => {
      // フォアグラウンド復帰したのでバックグラウンドフラグを下ろす
      isBackgroundedRef.current = false;
      tryResumeBgm();
      window.setTimeout(() => {
        tryResumeBgm();
      }, 300);
      window.setTimeout(() => {
        tryResumeBgm();
      }, 1000);
    };

    // Flutterからバックグラウンド遷移時に音声を一時停止するための関数
    (window as any).pauseAudio = () => {
      // バックグラウンドフラグを立てる（以降、復帰までBGMを再生しない）
      isBackgroundedRef.current = true;
      clearResumeVerifyTimeout();
      audioRef.current?.pause();
      for (const sound of Object.values(soundEffectsRef.current)) {
        sound.stop();
      }
    };

    // Flutterからバックグラウンド/フォアグラウンドの状態を明示的に通知するための関数
    // pauseAudio/resumeAudioと併用することもできるし、状態のみ更新したい時に使う
    (window as any).setBackgroundState = (isBackgrounded: boolean) => {
      isBackgroundedRef.current = isBackgrounded;
      if (isBackgrounded) {
        clearResumeVerifyTimeout();
        audioRef.current?.pause();
        for (const sound of Object.values(soundEffectsRef.current)) {
          sound.stop();
        }
      } else {
        tryResumeBgm();
      }
    };

    // 初回タップでオーディオをアンロックして再生開始（フォールバック）
    const handleFirstInteraction = () => {
      if (!isAudioUnlockedRef.current) {
        // まだセットアップされていない場合は通常のセットアップ
        isAudioUnlockedRef.current = true;
        setIsAudioUnlocked(true);
        setupBgm();
      } else if (!audioRef.current?.playing()) {
        // Flutterから先にunlockAndPlayAudioが呼ばれていてユーザージェスチャー無しで
        // autoplayが弾かれているケース。ユーザーの初回タップで再生を試みる。
        tryResumeBgm();
      }

      // イベントリスナーを削除
      document.removeEventListener('touchstart', handleFirstInteraction);
      document.removeEventListener('click', handleFirstInteraction);
    };

    document.addEventListener('touchstart', handleFirstInteraction);
    document.addEventListener('click', handleFirstInteraction);

    return () => {
      document.removeEventListener('touchstart', handleFirstInteraction);
      document.removeEventListener('click', handleFirstInteraction);
      delete (window as any).unlockAndPlayAudio;
      delete (window as any).resumeAudio;
      delete (window as any).pauseAudio;
      delete (window as any).setBackgroundState;
    };
  }, []);

  // ルート変更時にBGMを切り替え（room内はbgm_2、それ以外はbgm_1）
  React.useEffect(() => {
    if (!isAudioUnlockedRef.current) return;
    const isInRoom =
      router.pathname.startsWith('/rooms/') ||
      router.pathname.startsWith('/turn-rooms/');
    const targetBgm = isInRoom ? '/audios/bgm_2.mp3' : '/audios/bgm_1.mp3';
    changeBgm(targetBgm);
  }, [router.pathname]);

  // バックグラウンド遷移/復帰のBGM制御は Flutter 側の didChangeAppLifecycleState から
  // window.pauseAudio() / window.resumeAudio() を呼ぶ経路に一本化している。
  // （JS側の visibilitychange / pageshow / focus / watchdog はネイティブWebViewで
  // 取りこぼしが多かったため削除）

  // 全てのボタン押下で click_button の効果音を鳴らすためのグローバルリスナー
  // 個別のボタンコンポーネントに毎回書かなくて済むようにdocument委譲で実現する
  React.useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      // メイン以外のポインタは無視（中ボタンクリック等）
      if (e.button !== 0) return;
      const target = e.target as Element | null;
      if (!target) return;
      const btn = target.closest('button');
      if (!btn) return;
      // disabled の場合は鳴らさない
      if ((btn as HTMLButtonElement).disabled) return;
      // data-no-sound 属性が付いていれば鳴らさない（オプトアウト）
      if (btn.hasAttribute('data-no-sound')) return;
      if (isMutedSoundEffectRef.current) return;
      soundEffectsRef.current['click_button']?.play();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, []);

  const playBgm = () => {
    if (!audioRef.current) return;
    try {
      audioRef.current.play();
    } catch (error) {
      console.log('Error playing audio:', error);
      audioRef.current.once('unlock', () => {
        audioRef.current?.play();
      });
    }
  };

  const pauseBgm = () => {
    audioRef.current?.pause();
  };

  const stopBgm = () => {
    audioRef.current?.stop();
  };

  const toggleMute = () => {
    setMutedBGM(!isMutedBGM);
    if (isMutedBGM) {
      audioRef.current?.play();
      localStorage.setItem('bgmMuted', 'false');
    } else {
      audioRef.current?.stop();
      localStorage.setItem('bgmMuted', 'true');
    }
  };

  const toggleSoundEffectMute = () => {
    setMutedSoundEffect(!isMutedSoundEffect);
    if (isMutedSoundEffect) {
      localStorage.setItem('soundEffectMuted', 'false');
    } else {
      localStorage.setItem('soundEffectMuted', 'true');
    }
  };

  const changeBGMVolume = (volume: number) => {
    setBGMVolume(volume);
    // プリロード済みの全BGMにボリュームを反映
    for (const howl of Object.values(bgmHowlsRef.current)) {
      howl.volume(volume);
    }
    localStorage.setItem('bgmVolume', volume.toString());
  };

  const changeSoundEffectVolume = (volume: number) => {
    for (const sound of Object.values(soundEffectsRef.current)) {
      sound.volume(volume);
    }
    setSoundEffectVolume(volume);
    localStorage.setItem('soundEffectVolume', volume.toString());
  };

  const playSoundEffect = (key: string) => {
    if (isMutedSoundEffect) return;
    soundEffectsRef.current[key]?.play();
  };

  const stopSoundEffect = (key: string) => {
    soundEffectsRef.current[key]?.stop();
  };

  return (
    <AudioContext.Provider
      value={{
        bgm,
        bgmVolume,
        soundEffectVolume,
        isMutedBGM,
        isMutedSoundEffect,
        playBgm,
        pauseBgm,
        stopBgm,
        changeBgm,
        toggleMute,
        toggleSoundEffectMute,
        playSoundEffect,
        stopSoundEffect,
        changeBGMVolume,
        changeSoundEffectVolume,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = (): AudioContextType => {
  const context = React.useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};
